/** @jsxImportSource ./simboluri-jsx */
import { ReactNode } from "react";
import { numar, useT, useTx } from "../i18n";
import type { Cheie } from "../i18n";

import Info from "./Info";
import { CazLegenda } from "./tipuri";

// Ce înseamnă G1, G2a, Q1, S, E_0… — UN singur tabel, folosit de Pasul 8
// (unde se nasc cazurile) și de Pasul 11 (unde se combină). Textele vin din
// backend — `incarcari.legenda_cazuri` pentru cazurile gravitaționale,
// `combinatii.legenda_cazuri` le reia și adaugă seismul — deci aici e doar
// forma, niciodată explicația.
//
// Coloanele opționale sunt exact diferența dintre cele două pagini: la Pasul 8
// interesează UNDE cade încărcarea pe model, la Pasul 11 CU CE COEFICIENT
// intră în combinații.

export const ETICHETA_TIP: Record<string, Cheie> = {
  permanenta: "struct.caz.permanenta",
  variabila: "struct.caz.variabila",
  seismica: "struct.caz.seismica",
  torsiune: "struct.caz.torsiune",
  necunoscut: "struct.caz.necunoscut",
};

function kN(v: number | undefined): string {
  if (v === undefined || v === null) return "—";
  return numar(Math.round(v));
}

export default function LegendaCazuri({
  legenda,
  titlu = "struct.caz.titlu",
  unde = false,
  rol,
  notaCaz,
  avertisment,
  subsol,
}: {
  legenda: CazLegenda[];
  titlu?: Cheie;
  // coloana „unde se aplică pe model" (Pasul 8)
  unde?: boolean;
  // coloana „cum intră în combinații" (Pasul 11)
  rol?: (l: CazLegenda) => string;
  // linie proprie sub totalul unui caz (de exemplu panourile de subsol la G1)
  notaCaz?: (l: CazLegenda) => ReactNode;
  avertisment?: ReactNode;
  subsol?: ReactNode;
}) {
  const t = useT();
  const tx = useTx();
  if (!legenda?.length) return null;
  return (
    <div className="st-findings">
      <h3>
        {t(titlu)}
        <Info
          titlu={t("struct.caz.info_titlu")}
          ce={t("struct.caz.info_ce")}
          deCe={t("struct.caz.info_de_ce")}
        />
      </h3>
      <div className="sg-muted" style={{ marginBottom: 8, lineHeight: 1.55 }}>
        {tx("struct.caz.intro")}
        {avertisment}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="sg-table st-strat">
          <thead>
            <tr>
              <th>{t("struct.caz.col_simbol")}</th>
              <th>{t("struct.caz.col_tip")}</th>
              <th>{t("struct.caz.col_fizic")}</th>
              <th>{t("struct.caz.col_calcul")}</th>
              {unde && <th>{t("struct.caz.col_unde")}</th>}
              <th>{t("struct.caz.col_cat")}</th>
              {rol && <th>{t("struct.caz.col_rol")}</th>}
              <th>{t("struct.caz.col_normativ")}</th>
            </tr>
          </thead>
          <tbody>
            {legenda.map((l) => (
              <tr key={l.caz}>
                <td className="sg-mono">
                  <strong>{l.caz}</strong>
                </td>
                <td className="sg-muted">
                  {ETICHETA_TIP[l.tip] ? t(ETICHETA_TIP[l.tip]) : l.tip}
                </td>
                <td>{l.nume}</td>
                <td className="sg-muted">
                  {l.unitar ?? l.descriere ?? "—"}
                  {l.sursa && (
                    <div style={{ marginTop: 4 }}>
                      <em>{t("struct.caz.de_unde")}</em> {l.sursa}
                    </div>
                  )}
                </td>
                {unde && <td className="sg-muted">{l.unde ?? "—"}</td>}
                <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
                  {l.total_knm !== undefined
                    ? `${kN(l.total_knm)} kNm`
                    : l.total_kn !== undefined
                      ? `${kN(l.total_kn)} kN`
                      : "—"}
                  {l.total_eticheta && (
                    <div className="sg-muted">{l.total_eticheta}</div>
                  )}
                  {l.total_eticheta === undefined &&
                    l.total_kn !== undefined && (
                      <div className="sg-muted">{t("struct.caz.total_aplicat")}</div>
                    )}
                  {notaCaz?.(l)}
                </td>
                {rol && <td className="sg-muted">{rol(l)}</td>}
                <td className="sg-muted">{l.normativ ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {subsol}
    </div>
  );
}
